import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { registrationSchema, type RegistrationFormData } from '../lib/validation';
import { sendConfirmationEmail } from '../lib/email';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { createBucketIfNotExists, uploadFile } from '../lib/storage';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data: RegistrationFormData) => void;
}

function RegistrationModal({ isOpen, onClose, onSuccess }: RegistrationModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [tournaments, setTournaments] = useState<{id: string, name: string}[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<string>('');
  const [tournamentCategories, setTournamentCategories] = useState<{id: string, name: string}[]>([]);
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [currentRegistrations, setCurrentRegistrations] = useState(0);
  const [reservedSpots, setReservedSpots] = useState(0);
  const [isCapacityReached, setIsCapacityReached] = useState(false);
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [bankInformation, setBankInformation] = useState<{
    bank_name: string;
    account_name: string;
    account_number: string;
    sort_code?: string;
    swift_code?: string;
    is_primary: boolean;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    trigger,
    setValue
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    mode: 'all'
  });

  useEffect(() => {
    if (isOpen) {
      fetchTournamentData();
      fetchBankInformation();
      createBucketIfNotExists('paymentproofs', true);
    }
  }, [isOpen]);

  const fetchTournamentData = async () => {
    try {
      setIsLoading(true);

      const { data: registrationEnabledSetting } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'registration_enabled')
        .maybeSingle();

      if (registrationEnabledSetting) {
        const enabled = typeof registrationEnabledSetting.value === 'boolean'
          ? registrationEnabledSetting.value
          : registrationEnabledSetting.value === 'true' || registrationEnabledSetting.value === true;
        setIsRegistrationEnabled(enabled);

        if (!enabled) {
          setError('Registration is currently disabled by the tournament organizers.');
          setIsLoading(false);
          return;
        }
      }

      const { data: tournamentData, error: tError } = await supabase
        .from('tournaments')
        .select('id, name, status, capacity')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true });

      if (tError) throw tError;

      let capacity = 250;
      let fetchedReserved = 0;

      if (tournamentData && tournamentData.length > 0) {
        const upcomingTournaments = tournamentData.map(t => ({
          id: t.id,
          name: t.name
        }));

        setTournaments(upcomingTournaments);

        if (upcomingTournaments.length > 0) {
          const firstId = upcomingTournaments[0].id;
          setSelectedTournament(firstId);
          setValue('tournamentId', firstId);
          capacity = tournamentData[0].capacity || 250;
          setTotalCapacity(capacity);
          fetchCategoriesForTournament(firstId);
        }
      }

      const { data: settingsData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'reserved_spots')
        .maybeSingle();

      if (settingsData) {
        const parsed = typeof settingsData.value === 'number'
          ? settingsData.value
          : parseInt(String(settingsData.value));
        fetchedReserved = isNaN(parsed) ? 0 : parsed;
        setReservedSpots(fetchedReserved);
      }

      const tournamentId = tournamentData?.[0]?.id;
      let playerCount = 0;

      if (tournamentId) {
        const { count, error: countError } = await supabase
          .from('players')
          .select('*', { count: 'exact', head: true })
          .eq('tournament_id', tournamentId);

        if (!countError && count !== null) {
          playerCount = count;
          setCurrentRegistrations(count);
        }
      }

      setIsCapacityReached((playerCount + fetchedReserved) >= capacity);
    } catch (err) {
      console.error('Error fetching tournament data:', err);
      setError('Failed to load tournament data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBankInformation = async () => {
    try {
      const { data } = await supabase
        .from('bank_information')
        .select('*')
        .eq('is_primary', true)
        .eq('is_public', true)
        .maybeSingle();

      if (data) {
        setBankInformation(data);
        return;
      }

      const { data: anyBankData } = await supabase
        .from('bank_information')
        .select('*')
        .eq('is_public', true)
        .limit(1)
        .maybeSingle();

      if (anyBankData) {
        setBankInformation(anyBankData);
      }
    } catch (err) {
      console.error('Error fetching bank information:', err);
    }
  };

  const fetchCategoriesForTournament = async (tournamentId: string) => {
    const { data } = await supabase
      .from('tournament_categories')
      .select('id, name')
      .eq('tournament_id', tournamentId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    setTournamentCategories(data || []);
  };

  const handleClose = () => {
    setCurrentStep(1);
    setError('');
    setShowConfirmation(false);
    setSelectedFile(null);
    setRegistrationNumber('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    reset();
    onClose();
  };

  const handleTournamentChange = async (tournamentId: string) => {
    setSelectedTournament(tournamentId);
    setValue('tournamentId', tournamentId);
    setValue('category', '');

    if (!tournamentId) {
      setTournamentCategories([]);
      return;
    }

    fetchCategoriesForTournament(tournamentId);

    const { count } = await supabase
      .from('players')
      .select('*', { count: 'exact', head: true })
      .eq('tournament_id', tournamentId);

    const playerCount = count ?? 0;
    setCurrentRegistrations(playerCount);
    setIsCapacityReached((playerCount + reservedSpots) >= totalCapacity);
  };

  const handleNext = async () => {
    const isStepValid = await trigger([
      'firstName',
      'lastName',
      'sex',
      'category',
      'residentClub',
      'handicap',
      'phone',
      'email',
      'shirtSize',
      'tournamentId'
    ], { shouldFocus: true });

    if (isStepValid) {
      if (!isRegistrationEnabled) {
        toast.error('Registration is currently disabled.');
        return;
      }

      if ((currentRegistrations + reservedSpots) >= totalCapacity) {
        toast.error('Registration is closed. Maximum capacity has been reached.');
        return;
      }

      setCurrentStep(2);
    }
  };

  const onSubmit = async (data: RegistrationFormData) => {
    try {
      setIsSubmitting(true);
      setError('');

      if (!isRegistrationEnabled) {
        throw new Error('Registration is currently disabled.');
      }

      if ((currentRegistrations + reservedSpots) >= totalCapacity) {
        throw new Error('Registration is closed. Maximum capacity has been reached.');
      }

      if (!data.transferDate || !data.senderName) {
        setError('Please fill in all payment details');
        return;
      }

      if (!selectedFile) {
        setError('Please upload your proof of payment before submitting');
        return;
      }

      const regNumber = `IBO-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      setRegistrationNumber(regNumber);

      let paymentProofUrl = '';
      if (selectedFile) {
        try {
          await createBucketIfNotExists('paymentproofs', true);

          paymentProofUrl = await uploadFile(
            selectedFile,
            'paymentproofs',
            'players'
          );
        } catch (uploadError) {
          throw new Error(`Error uploading payment proof:\n\n${uploadError instanceof Error ? uploadError.message : String(uploadError)}`);
        }
      }

      const { error: playerError } = await supabase
        .from('players')
        .insert([{
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          phone: data.phone,
          country: 'Nigeria',
          resident_club: data.residentClub,
          handicap: parseInt(data.handicap),
          category: data.category,
          tournament_id: data.tournamentId,
          gender: data.sex,
          registration_status: {
            personalInfo: true,
            payment: false,
            verification: false
          },
          status: 'pending',
          payment_proof_url: paymentProofUrl,
          shirt_size: data.shirtSize,
          registration_number: regNumber
        }]);

      if (playerError) {
        throw new Error(`Failed to register player: ${playerError.message}`);
      }

      await sendConfirmationEmail(data.email, data.firstName, regNumber, data.shirtSize, data.lastName);

      setShowConfirmation(true);
      setCurrentStep(3);

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred during registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
        setError('File must be an image (JPEG, PNG) or PDF');
        return;
      }
      setSelectedFile(file);
      setValue('proofOfPayment', file);
      setError('');
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
        setError('File must be an image (JPEG, PNG) or PDF');
        return;
      }
      setSelectedFile(file);
      setValue('proofOfPayment', file);
      setError('');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const removeFile = () => {
    setSelectedFile(null);
    setValue('proofOfPayment', undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const watchedFields = watch();
  const isStep1Valid = Boolean(
    watchedFields.firstName &&
    watchedFields.lastName &&
    watchedFields.sex &&
    watchedFields.residentClub &&
    watchedFields.handicap &&
    watchedFields.phone &&
    watchedFields.email &&
    watchedFields.shirtSize &&
    watchedFields.tournamentId &&
    !errors.firstName &&
    !errors.lastName &&
    !errors.sex &&
    !errors.residentClub &&
    !errors.handicap &&
    !errors.phone &&
    !errors.email &&
    !errors.shirtSize &&
    !errors.tournamentId
  );

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="absolute right-0 top-0 pr-4 pt-4 z-10">
                  <button
                    type="button"
                    className="rounded-full bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {showConfirmation ? (
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="text-center py-8">
                      <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-green-100 mb-6">
                        <svg className="h-12 w-12 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-display font-bold text-golf-gray mb-4">
                        Registration Successful!
                      </h3>
                      <p className="text-gray-600 mb-2">
                        Thank you for registering for the tournament.
                      </p>
                      <p className="text-gray-600 mb-2">
                        Your registration number is: <span className="font-bold">{registrationNumber}</span>
                      </p>
                      <p className="text-gray-600 mb-8">
                        We've sent a confirmation email to your registered email address with further instructions.
                      </p>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="modern-button px-8 py-3 text-white text-lg font-medium"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="border-b border-gray-200 bg-golf-sand/30 px-4 py-5 sm:px-6">
                      <Dialog.Title as="h3" className="text-2xl font-display font-bold text-golf-gray pr-8">
                        Player Registration
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-600">
                        {currentStep === 1 ? 'Step 1: Personal Information' : 'Step 2: Payment Details'}
                      </p>

                      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                        <span>Registration Capacity:</span>
                        <span>{currentRegistrations + reservedSpots}/{totalCapacity} players</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div
                          className="bg-golf-orange rounded-full h-1.5"
                          style={{ width: `${Math.min(((currentRegistrations + reservedSpots) / totalCapacity) * 100, 100)}%` }}
                        />
                      </div>
                      {isCapacityReached && (
                        <p className="mt-1 text-xs text-red-500">
                          Registration is at full capacity. Please check back later.
                        </p>
                      )}
                    </div>

                    <div className="px-4 py-5 sm:p-6 space-y-6">
                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-600">
                          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                          <p className="text-sm">{error}</p>
                        </div>
                      )}

                      {currentStep === 1 ? (
                        <div className="space-y-6">
                          <div>
                            <label htmlFor="tournamentId" className="block text-sm font-medium text-gray-700">
                              Tournament <span className="text-red-500">*</span>
                            </label>
                            <select
                              id="tournamentId"
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                              value={selectedTournament}
                              onChange={(e) => handleTournamentChange(e.target.value)}
                            >
                              <option value="">Select a tournament</option>
                              {tournaments.map((tournament) => (
                                <option key={tournament.id} value={tournament.id}>
                                  {tournament.name}
                                </option>
                              ))}
                            </select>
                            {errors.tournamentId && (
                              <p className="mt-1 text-sm text-red-600">{errors.tournamentId.message}</p>
                            )}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                First Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                {...register("firstName")}
                                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                              />
                              {errors.firstName && (
                                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                              )}
                            </div>

                            <div>
                              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                Last Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                {...register("lastName")}
                                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                              />
                              {errors.lastName && (
                                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                              )}
                            </div>
                          </div>

                          <div>
                            <label htmlFor="sex" className="block text-sm font-medium text-gray-700">
                              Gender <span className="text-red-500">*</span>
                            </label>
                            <select
                              {...register("sex")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                            >
                              <option value="">Select gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                            </select>
                            {errors.sex && (
                              <p className="mt-1 text-sm text-red-600">{errors.sex.message}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                              Playing Category <span className="text-red-500">*</span>
                            </label>
                            <select
                              {...register("category")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                            >
                              <option value="">Select category</option>
                              {tournamentCategories.map((cat) => (
                                <option key={cat.id} value={cat.name}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                            {tournamentCategories.length === 0 && selectedTournament && (
                              <p className="mt-1 text-xs text-gray-500">No categories defined for this tournament yet.</p>
                            )}
                            {errors.category && (
                              <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="residentClub" className="block text-sm font-medium text-gray-700">
                              Resident Club <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              {...register("residentClub")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                            />
                            {errors.residentClub && (
                              <p className="mt-1 text-sm text-red-600">{errors.residentClub.message}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="handicap" className="block text-sm font-medium text-gray-700">
                              Handicap <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="36"
                              {...register("handicap")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                            />
                            {errors.handicap && (
                              <p className="mt-1 text-sm text-red-600">{errors.handicap.message}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="shirtSize" className="block text-sm font-medium text-gray-700">
                              Shirt Size <span className="text-red-500">*</span>
                            </label>
                            <select
                              {...register("shirtSize")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                            >
                              <option value="">Select shirt size</option>
                              <option value="XS">XS - Extra Small</option>
                              <option value="S">S - Small</option>
                              <option value="M">M - Medium</option>
                              <option value="L">L - Large</option>
                              <option value="XL">XL - Extra Large</option>
                              <option value="XXL">XXL - Extra Extra Large</option>
                              <option value="XXXL">XXXL - Triple Extra Large</option>
                            </select>
                            {errors.shirtSize && (
                              <p className="mt-1 text-sm text-red-600">{errors.shirtSize.message}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                              Phone Number <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              {...register("phone")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                              placeholder="+234 800 000 0000"
                            />
                            {errors.phone && (
                              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              {...register("email")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                            />
                            {errors.email && (
                              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          <div>
                            <h4 className="text-lg font-medium text-golf-gray mb-4">Payment Information</h4>
                            <p className="text-sm text-gray-600 mb-6">
                              Please make a payment of <span className="font-bold">{'\u20A6'}100,000</span> to the following account:
                            </p>
                            {bankInformation ? (
                              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                <p className="text-sm text-gray-700">Bank: <span className="font-medium">{bankInformation.bank_name}</span></p>
                                <p className="text-sm text-gray-700">Account Name: <span className="font-medium">{bankInformation.account_name}</span></p>
                                <p className="text-sm text-gray-700">Account Number: <span className="font-medium">{bankInformation.account_number}</span></p>
                                {bankInformation.sort_code && (
                                  <p className="text-sm text-gray-700">Sort Code: <span className="font-medium">{bankInformation.sort_code}</span></p>
                                )}
                              </div>
                            ) : (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                                <p className="text-sm text-amber-700">
                                  Bank account details are currently unavailable. Please contact the organizers for payment information.
                                </p>
                              </div>
                            )}
                          </div>

                          <div>
                            <label htmlFor="transferDate" className="block text-sm font-medium text-gray-700">
                              Date of Transfer <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              {...register("transferDate")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                            />
                            {errors.transferDate && (
                              <p className="mt-1 text-sm text-red-600">{errors.transferDate.message}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="senderName" className="block text-sm font-medium text-gray-700">
                              Sender's Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              {...register("senderName")}
                              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-golf-orange focus:border-golf-orange"
                            />
                            {errors.senderName && (
                              <p className="mt-1 text-sm text-red-600">{errors.senderName.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Proof of Payment <span className="text-red-500">*</span>
                            </label>
                            <div
                              className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg ${
                                selectedFile ? 'border-golf-orange bg-golf-orange/5' : 'border-gray-300 hover:border-golf-orange'
                              }`}
                              onDrop={handleFileDrop}
                              onDragOver={handleDragOver}
                            >
                              <div className="space-y-1 text-center">
                                {selectedFile ? (
                                  <div className="flex flex-col items-center">
                                    <svg className="mx-auto h-12 w-12 text-golf-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <p className="text-sm text-gray-600 mt-2">
                                      {selectedFile.name}
                                    </p>
                                    <button
                                      type="button"
                                      onClick={removeFile}
                                      className="mt-2 text-sm text-red-600 hover:text-red-800"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                      />
                                    </svg>
                                    <div className="flex text-sm text-gray-600">
                                      <label
                                        htmlFor="file-upload"
                                        className="relative cursor-pointer rounded-md font-medium text-golf-orange hover:text-golf-green focus-within:outline-none"
                                      >
                                        <span>Upload a file</span>
                                        <input
                                          id="file-upload"
                                          name="file-upload"
                                          type="file"
                                          className="sr-only"
                                          ref={fileInputRef}
                                          onChange={handleFileSelect}
                                          accept="image/jpeg,image/png,application/pdf"
                                        />
                                      </label>
                                      <p className="pl-1">or drag and drop</p>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                      PNG, JPG, PDF up to 10MB
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                            {errors.proofOfPayment && (
                              <p className="mt-1 text-sm text-red-600">{String(errors.proofOfPayment.message)}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      {currentStep === 1 ? (
                        <>
                          <button
                            type="button"
                            onClick={handleNext}
                            disabled={!isStep1Valid || isCapacityReached}
                            className="modern-button w-full sm:w-auto px-8 py-3 text-white sm:ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                          <button
                            type="button"
                            className="mt-3 sm:mt-0 w-full sm:w-auto px-8 py-3 bg-white text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange"
                            onClick={handleClose}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="submit"
                            disabled={isSubmitting || isCapacityReached || !selectedFile}
                            className="modern-button w-full sm:w-auto px-8 py-3 text-white sm:ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                          </button>
                          <button
                            type="button"
                            className="mt-3 sm:mt-0 w-full sm:w-auto px-8 py-3 bg-white text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange"
                            onClick={() => setCurrentStep(1)}
                          >
                            Back
                          </button>
                        </>
                      )}
                    </div>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default RegistrationModal;
